import { useEffect, useState, type FormEvent } from 'react';
import { grantAdmin, listAdmins, revokeAdmin } from '../features/admins/api';
import type { AdminRow } from '../lib/types';
import { useAuth } from '../lib/useAuth';
import { friendlyErrorMessage } from '../lib/errors';
import { useToast } from '../components/Toast';
import { Button } from '../components/Button';
import { Spinner } from '../components/Spinner';
import { ConfirmDialog } from '../components/ConfirmDialog';

export function AdminsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [admins, setAdmins] = useState<AdminRow[] | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [toRevoke, setToRevoke] = useState<AdminRow | null>(null);

  function refresh() {
    listAdmins().then(setAdmins).catch(() => setAdmins([]));
  }

  useEffect(refresh, []);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await grantAdmin(email);
      setEmail('');
      toast.show('Admin added', 'success');
      refresh();
    } catch (err) {
      toast.show(friendlyErrorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke() {
    if (!toRevoke) return;
    try {
      await revokeAdmin(toRevoke.user_id);
      toast.show('Admin removed', 'success');
      refresh();
    } catch (err) {
      toast.show(friendlyErrorMessage(err), 'error');
    } finally {
      setToRevoke(null);
    }
  }

  return (
    <div>
      <div className="topbar">
        <h1 className="page-title">Admins</h1>
      </div>

      <form className="card" style={{ maxWidth: 480, marginBottom: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }} onSubmit={handleAdd}>
        <input
          className="input"
          style={{ flex: 1, minWidth: 220 }}
          type="email"
          required
          placeholder="person@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button type="submit" disabled={busy} style={{ flex: 'initial' }}>
          Add admin
        </Button>
      </form>

      {admins === null ? (
        <Spinner />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Added</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.user_id}>
                <td>{a.email}</td>
                <td className="muted">{new Date(a.created_at).toLocaleDateString()}</td>
                <td>
                  {a.user_id === user?.id ? (
                    <span className="muted">You</span>
                  ) : (
                    <Button tone="danger" size="sm" onClick={() => setToRevoke(a)}>
                      Remove
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmDialog
        open={Boolean(toRevoke)}
        title="Remove admin"
        message={`Remove admin access for ${toRevoke?.email}?`}
        confirmLabel="Remove"
        onConfirm={handleRevoke}
        onCancel={() => setToRevoke(null)}
      />
    </div>
  );
}
